import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminEmailFlowRunsQuery } from '@/generated/graphql';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import RunDetailsDialog from './RunDetailsDialog';

const PAGE_SIZE = 25;

const StatusBadge = ({ status }: { status: string }) => {
  const variant = status === 'completed' ? 'default'
    : status === 'failed' ? 'destructive'
    : 'secondary';
  return <Badge variant={variant as any}>{status}</Badge>;
};

const RunsCard = ({ flowId }: { flowId: string }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const { data, loading } = useAdminEmailFlowRunsQuery({
    variables: { flowId, page, pageSize: PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
  });
  const rows = data?.adminEmailFlowRuns?.data ?? [];
  const total = data?.adminEmailFlowRuns?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Card>
      <CardHeader><CardTitle>{t('admin.emailFlows.runs.title')}</CardTitle></CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.emailFlows.runs.user')}</TableHead>
              <TableHead>{t('admin.emailFlows.runs.started')}</TableHead>
              <TableHead>{t('admin.emailFlows.runs.status')}</TableHead>
              <TableHead>{t('admin.emailFlows.runs.sends')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setOpenRunId(r.id)}>
                <TableCell>{r.user_email || '—'}</TableCell>
                <TableCell className="text-sm">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell>{r.sendsCount}</TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">{t('admin.emailFlows.runs.empty')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <div className="flex items-center justify-end gap-2 mt-3">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>{'<'}</Button>
          <span className="text-sm">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>{'>'}</Button>
        </div>
        <RunDetailsDialog runId={openRunId} onOpenChange={(v) => !v && setOpenRunId(null)} />
      </CardContent>
    </Card>
  );
};

export default RunsCard;
